import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as gis from './gisClient';
import { completeRedirect, RedirectError } from './gisRedirect';

/**
 * Where Google sends the user back after a redirect sign-in.
 *
 * Unlike dropboxRedirect this is not a popup talking to an opener - it is the
 * app itself, mid-navigation, and it is the only page loaded at this point.
 * So it takes the token out of the fragment, hands it to gisClient, and pushes
 * the user back where they were.
 *
 * It deliberately renders almost nothing. Anything heavier would be a second
 * thing that can fail between the user having a valid token and the app
 * knowing about it.
 */
export default function GoogleRedirect() {

  const history = useHistory();
  const [error, setError] = useState<string>();
  const [returnTo, setReturnTo] = useState('/app');

  // The work is a one-shot side effect; a re-run would find the fragment
  // already consumed and report a spurious failure.
  const handled = useRef(false);

  useEffect(function completeGoogleSignIn() {
    if (handled.current) {
      return;
    }

    handled.current = true;

    try {
      const result = completeRedirect(window.location.hash, window.location.search);

      gis.adoptRedirectToken(result);

      // replace, not push: the URL still carries the access token in its
      // fragment, and there is no reason to leave that in history or to let
      // Back land on a callback that can no longer be completed.
      history.replace(result.returnTo);
    }
    catch (err) {
      const failure = err as RedirectError;

      setReturnTo(failure.returnTo || '/app');
      setError(failure.message || 'Google sign-in failed.');
    }
  }, [history]);

  if (!error) {
    return <p>Completing Google sign-in…</p>;
  }

  return (
    <article className="mainViewport">
      <section>
        <h5>Google sign-in did not complete</h5>
        <p>{error}</p>
        <button
          className="button"
          onClick={() => history.replace(returnTo)}
        >
          Back to Fadila
        </button>
      </section>
    </article>
  );
}
